import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "@/lib/constants";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import {
  Calendar,
  Clock,
  MapPin,
  Package,
  Navigation,
  User,
  Phone,
  DollarSign,
  Truck,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
// import { MapPicker } from '@/components/features/shipments/wizard/MapPicker';
import { useApi } from "@/hooks/useApi";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { Shipment } from "@/types/shipment.types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Extended interface for public tracking API response
interface PublicShipmentResponse extends Omit<Shipment, "status_code" | "payment_type" | "actual_payment_type" | "cod_amount"> {
  status?: {
    code: string;
    color: string;
    name_: string;
  };
  status_code?: string;
  actual_payment_type?: string;
  tenant?: {
    id: string;
    name: string;
    phone: string;
    address: string | null;
  };
  carrier?: {
    id: string;
    name: string;
    logo_url: string;
    contact_info: {
      email: string;
      phone: string;
      address: string;
    };
  };
  driver?: {
    name: string;
    phone: string;
  };
  delivery_fee?: string;
  platform_fee?: string;
  tenant_payout?: string;
  cod_amount?: string | null;
  total_amount?: string;
  delivered_at?: string | null;
  available_actions?: Array<{
    code: string;
    name: string;
    color: string;
  }>;
}

const TOKEN_KEY = (tn: string) => `QuBox_track_token_${tn}`;

export default function CustomerShipmentTracking() {
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action"); // e.g. 'verify-location'

  const [shipment, setShipment] = useState<PublicShipmentResponse | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [location, setLocation] = useState({ lat: 0, lng: 0 });
  const [address, setAddress] = useState({ street: "", neighborhood: "", city: "" });
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // PIN gate state
  const [pinRequired, setPinRequired] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [token, setToken] = useState<string | null>(() =>
    trackingNumber ? sessionStorage.getItem(TOKEN_KEY(trackingNumber)) : null,
  );

  const { post, loading } = useApi();

  // Action toggle (e.g. ?action=verify-location)
  const [activeAction, setActiveAction] = useState<string | null>(initialAction);

  useEffect(() => {
    if (trackingNumber) {
      fetchShipmentDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingNumber, token]);

  const fetchShipmentDetails = async () => {
    if (!trackingNumber) return;
    try {
      const headers: Record<string, string> = {};
      if (token) headers["x-shipment-token"] = token;
      const res = await axios.get(`${API_BASE_URL}/api/v1/public/track/${trackingNumber}`, { headers });
      const response = res.data;
      console.log("API Response:", response);
      console.log("Response type:", typeof response);
      console.log("Response keys:", response ? Object.keys(response) : "null/undefined");

      if (!response) {
        console.error("Response is null or undefined");
        toast.error("فشل في تحميل بيانات الشحنة");
        return;
      }

      // Handle both wrapped and unwrapped responses
      const shipmentData = response?.data || response;
      console.log("Shipment Data:", shipmentData);

      if (shipmentData && shipmentData.id) {
        setShipment(shipmentData);
        // Pre-fill delivery address if available
        if (shipmentData.to_address) {
          setLocation({
            lat: shipmentData.to_address.lat,
            lng: shipmentData.to_address.lng,
          });
          setAddress({
            street: shipmentData.to_address.street,
            neighborhood: shipmentData.to_address.neighborhood,
            city: shipmentData.to_address.city,
          });
        }
      } else {
        console.error("No valid shipment data found");
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) {
        // PIN gate triggered — clear bad token and prompt
        setPinRequired(true);
        if (trackingNumber) sessionStorage.removeItem(TOKEN_KEY(trackingNumber));
        setToken(null);
        return;
      }
      console.error("Error fetching shipment:", error);
      toast.error("حدث خطأ أثناء تحميل بيانات الشحنة");
    }
  };

  const handleVerifyPin = async () => {
    if (!trackingNumber) return;
    if (!/^\d{4}$/.test(pinValue)) {
      setPinError("الرمز يجب أن يكون 4 أرقام");
      return;
    }
    setPinSubmitting(true);
    setPinError(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/public/track/${trackingNumber}/verify-pin`,
        { pin: pinValue },
      );
      const newToken: string | undefined = res.data?.data?.token;
      if (!newToken) {
        setPinError("استجابة غير متوقعة من الخادم");
        return;
      }
      sessionStorage.setItem(TOKEN_KEY(trackingNumber), newToken);
      setToken(newToken);
      setPinRequired(false);
      setPinValue("");
      toast.success("تم التحقق بنجاح");
    } catch (error: any) {
      const msg = error?.response?.data?.message || "رمز غير صحيح";
      setPinError(msg);
    } finally {
      setPinSubmitting(false);
    }
  };

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          toast.success("تم تحديد موقعك بنجاح");
          setIsGettingLocation(false);
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error("فشل الحصول على الموقع. يرجى السماح بالوصول للموقع");
          setIsGettingLocation(false);
        },
      );
    } else {
      toast.error("المتصفح لا يدعم خاصية تحديد الموقع");
      setIsGettingLocation(false);
    }
  };

  const handleLocationChange = (lat: number, lng: number, city?: string, street?: string) => {
    setLocation({ lat, lng });
    if (city || street) {
      setAddress((prev) => ({
        ...prev,
        ...(city && { city }),
        ...(street && { street }),
      }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("يرجى اختيار تاريخ ووقت الاستلام");
      return;
    }

    if (location.lat === 0 || location.lng === 0) {
      toast.error("يرجى تحديد موقع الاستلام");
      return;
    }

    try {
      const headers: Record<string, string> = {};
      if (token) headers["x-shipment-token"] = token;
      await axios.post(
        `${API_BASE_URL}/api/v1/public/shipments/${trackingNumber}/delivery-preferences`,
        {
          delivery_date: format(selectedDate, "yyyy-MM-dd"),
          delivery_time: selectedTime,
          location,
          address,
        },
        { headers },
      );
      toast.success("تم حفظ تفضيلات التوصيل بنجاح");
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) {
        setPinRequired(true);
        return;
      }
      console.error("Submit error:", error);
      toast.error("فشل حفظ التفضيلات");
    }
  };

  const timeSlots = ["09:00 - 12:00", "12:00 - 15:00", "15:00 - 18:00", "18:00 - 21:00"];

  // Helper function to get status badge variant
  const getStatusVariant = (statusCode?: string): "default" | "secondary" | "destructive" | "outline" => {
    if (!statusCode) return "secondary";
    const code = statusCode.toLowerCase();
    if (code.includes("delivered") || code.includes("completed")) return "default";
    if (code.includes("cancelled") || code.includes("failed")) return "destructive";
    if (code.includes("pending") || code.includes("assigned")) return "secondary";
    return "outline";
  };

  // Helper function to get status icon
  const getStatusIcon = (statusCode?: string) => {
    if (!statusCode) return Package;
    const code = statusCode.toLowerCase();
    if (code.includes("delivered") || code.includes("completed")) return CheckCircle2;
    if (code.includes("cancelled") || code.includes("failed")) return XCircle;
    if (code.includes("assigned")) return Truck;
    return Package;
  };

  // PIN gate — shown until the customer enters the correct 4-digit PIN
  if (pinRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4" dir="rtl">
        <Card className="max-w-md w-full shadow-lg border-2">
          <CardHeader className="text-center space-y-3 pb-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">تأكيد الشحنة</CardTitle>
            <CardDescription>
              يرجى إدخال رمز التحقق المرسل إلى رقم هاتفك عبر واتساب
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin-input">رمز التحقق (4 أرقام)</Label>
              <Input
                id="pin-input"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={pinValue}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setPinValue(v);
                  if (pinError) setPinError(null);
                }}
                placeholder="••••"
                className="text-center text-3xl font-mono tracking-widest h-14"
                autoFocus
                disabled={pinSubmitting}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && pinValue.length === 4 && !pinSubmitting) handleVerifyPin();
                }}
              />
              {pinError && <p className="text-sm text-destructive">{pinError}</p>}
            </div>
            <Button
              className="w-full h-12 text-base"
              onClick={handleVerifyPin}
              disabled={pinSubmitting || pinValue.length !== 4}
            >
              {pinSubmitting ? "جارٍ التحقق..." : "تأكيد"}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              رقم التتبع: <span className="font-mono">{trackingNumber}</span>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <LoadingSkeleton type="card" />
          <LoadingSkeleton type="card" />
          <LoadingSkeleton type="card" />
        </div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg border-2">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <Package className="h-8 w-8 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-2xl">لم يتم العثور على الشحنة</CardTitle>
              <CardDescription className="mt-2 text-base">رقم التتبع غير صحيح أو الشحنة غير موجودة</CardDescription>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(shipment.status?.code || shipment.status_code);
  const statusCode = shipment.status?.code || shipment.status_code;
  const statusVariant = getStatusVariant(statusCode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-8" dir="rtl">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header with Carrier Logo */}
        <div className="text-center space-y-6">
          {shipment.carrier?.logo_url && (
            <div className="flex justify-center">
              <Avatar className="h-20 w-20 md:h-24 md:w-24 border-4 border-background shadow-xl">
                <AvatarImage src={shipment.carrier.logo_url} alt={shipment.carrier.name} />
                <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-primary to-primary/80 text-white">
                  {shipment.carrier.name.substring(0, 2)}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
          <div className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              تتبع شحنتك
            </h1>
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Package className="h-4 w-4" />
              <p className="text-sm md:text-base">
                رقم التتبع: <span className="font-semibold text-foreground">{shipment.tracking_number}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Status Card - Prominent Display */}
        <Card className="border-2 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-lg">
                  <StatusIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">حالة الشحنة</p>
                  <Badge variant={statusVariant} className="text-base px-4 py-1">
                    {shipment.status?.name_ || statusCode}
                  </Badge>
                </div>
              </div>
              {shipment.carrier && (
                <div className="text-end">
                  <p className="text-sm text-muted-foreground mb-1">شركة الشحن</p>
                  <p className="font-semibold text-lg">{shipment.carrier.name}</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Shipment Details Cards - Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recipient Information */}
          <Card className="shadow-md hover:shadow-xl transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/10">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                معلومات المستلم
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">الاسم</Label>
                  <p className="font-semibold text-base">{shipment.recipient_info.name}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">رقم الهاتف</Label>
                  <p className="font-semibold text-base" dir="ltr">
                    {shipment.recipient_info.phone}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipment Value & Payment */}
          <Card className="shadow-md hover:shadow-xl transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/10">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                معلومات الدفع
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">قيمة الشحنة</Label>
                  <p className="font-bold text-xl text-primary">{shipment.shipment_value}</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-start gap-3">
                <Package className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">نوع الدفع</Label>
                  <p className="font-semibold text-base">{shipment.actual_payment_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Delivery Address Card */}
        <Card className="shadow-md hover:shadow-xl transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/10">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              عنوان التوصيل
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">المدينة</Label>
                <p className="font-semibold text-base">{shipment.to_address.city}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">الحي</Label>
                <p className="font-semibold text-base">{shipment.to_address.neighborhood}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">الشارع</Label>
                <p className="font-semibold text-base">{shipment.to_address.street}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items List */}
        {shipment.items && shipment.items.length > 0 && (
          <Card className="shadow-md hover:shadow-xl transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-600/10">
                  <Package className="h-5 w-5 text-orange-600" />
                </div>
                محتويات الشحنة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {shipment.items.map((item, index) => (
                  <div key={item.id}>
                    {index > 0 && <Separator className="my-3" />}
                    <div className="flex justify-between items-center p-3 rounded-lg bg-gradient-to-r from-muted/50 to-muted/30 hover:from-muted/70 hover:to-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        الكمية: {item.quantity}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery Preferences */}
        <Card className="shadow-md hover:shadow-xl transition-shadow border-2">
          <CardHeader className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500/10 to-cyan-600/10">
                <Clock className="h-5 w-5 text-cyan-600" />
              </div>
              <CardTitle className="text-xl">تفضيلات التوصيل</CardTitle>
            </div>
            <CardDescription className="text-base">اختر الوقت والمكان المناسب لاستلام شحنتك</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {/* Date and Time Selection - Side by Side */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Date Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Calendar className="h-5 w-5 text-primary" />
                  تاريخ الاستلام المفضل
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-end font-normal h-12 border-2 hover:border-primary transition-colors",
                        !selectedDate && "text-muted-foreground",
                      )}
                    >
                      <Calendar className="ms-2 h-5 w-5" />
                      {selectedDate ? format(selectedDate, "PPP", { locale: ar }) : "اختر التاريخ"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Clock className="h-5 w-5 text-primary" />
                  الفترة الزمنية المفضلة
                </Label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="w-full h-12 border-2 hover:border-primary transition-colors">
                    <SelectValue placeholder="اختر الفترة الزمنية" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot} className="text-base">
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Address Details */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <MapPin className="h-5 w-5 text-primary" />
                  موقع التوصيل
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={getCurrentLocation}
                  disabled={isGettingLocation}
                  className="gap-2 border-2 hover:border-primary hover:bg-primary hover:text-primary-foreground transition-all"
                >
                  <Navigation className="h-4 w-4" />
                  {isGettingLocation ? "جاري التحديد..." : "موقعي الحالي"}
                </Button>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">المدينة</Label>
                  <Input
                    value={address.city}
                    onChange={(e) => setAddress((prev) => ({ ...prev, city: e.target.value }))}
                    placeholder="الرياض"
                    className="h-11 border-2 focus:border-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">الحي</Label>
                  <Input
                    value={address.neighborhood}
                    onChange={(e) => setAddress((prev) => ({ ...prev, neighborhood: e.target.value }))}
                    placeholder="العليا"
                    className="h-11 border-2 focus:border-primary transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">الشارع</Label>
                  <Input
                    value={address.street}
                    onChange={(e) => setAddress((prev) => ({ ...prev, street: e.target.value }))}
                    placeholder="شارع الملك فهد"
                    className="h-11 border-2 focus:border-primary transition-colors"
                  />
                </div>
              </div>

              {/* <MapPicker
                lat={location.lat}
                lng={location.lng}
                onLocationChange={handleLocationChange}
                label="حدد الموقع على الخريطة"
              /> */}
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary"
              size="lg"
            >
              <CheckCircle2 className="ms-2 h-5 w-5" />
              حفظ تفضيلات التوصيل
            </Button>
          </CardContent>
        </Card>

        {/* Contact Information (if carrier info is available) */}
        {shipment.carrier?.contact_info && (
          <Card className="shadow-md bg-gradient-to-br from-muted/30 to-muted/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-lg">
                <Phone className="h-5 w-5 text-primary" />
                تواصل معنا
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {shipment.carrier.contact_info.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">الهاتف:</span>
                    <span className="font-semibold" dir="ltr">
                      {shipment.carrier.contact_info.phone}
                    </span>
                  </div>
                )}
                {shipment.carrier.contact_info.email && (
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">البريد:</span>
                    <span className="font-semibold" dir="ltr">
                      {shipment.carrier.contact_info.email}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
